// mds-dojo — dijit/_TemplatedMixin — FAITHFUL port of references/dijit/_TemplatedMixin.js.
// Codemod pipeline; finished: declare from the faithful runtime; fixed the transform's
// first-`return` mis-rewrite in _stringRepl; `dojo/cache` dropped (widgets always
// provide templateString via `import t from './x.html?raw'`, so the templatePath
// fallback never runs); `dojo/sniff` → `dojo/has` (only has("ie") is read, and its
// IE unload-cleanup block is dropped); getCachedTemplate strips HTML comments before
// ${} substitution / toDom (C17 — ?raw keeps comments dojo's build stripped, and a
// commented-out ${key} would otherwise throw). Promoted to canonical.
import declare from '../dojo/_base/declare-runtime';
import domConstruct from '../dojo/dom-construct';
import lang from '../dojo/_base/lang';
import string from '../dojo/string';
import _AttachMixin from './_AttachMixin';

const _TemplatedMixin = declare('dijit._TemplatedMixin', _AttachMixin, {
  templateString: null,
  templatePath: null,
  _skipNodeCache: false,

  // back-compat for attach-points/events inside this.containerNode (remove for 2.0)
  searchContainerNode: true,

  _stringRepl: function (tmpl: string) {
    // ${foo} property substitution in the template string.
    var className = this.declaredClass, _this = this;
    return string.substitute(tmpl, this, function (this: any, value: any, key: string) {
      if (key.charAt(0) == '!') { value = lang.getObject(key.substr(1), false, _this); }
      if (typeof value == 'undefined') {
        // dojo THREW here (typo guard). In webtool_eGo, ${translations.*} keys come from
        // the PIT backend and are occasionally absent (e.g. WebCalendar_SidebarLabel_Date):
        // a single missing key must not kill the whole widget (it took out the calendar).
        // Warn — keeps the signal for a backend/typo fix — and fall back to empty so the
        // rest of the template still renders.
        console.warn(className + ' template: missing ${' + key + '} — rendered empty (add the translation or fix the key)');
        return '';
      }
      if (value == null) { return ''; }
      // keys beginning with ! skip escaping (unescaped markup, e.g. ${!foo})
      return key.charAt(0) == '!' ? value : this._escapeValue('' + value);
    }, this);
  },

  _escapeValue: function (val: string) {
    return val.replace(/["'<>&]/g, function (v: string) {
      return ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
      } as any)[v];
    });
  },

  buildRendering: function () {
    // Construct the UI from a template, setting this.domNode.
    if (!this._rendered) {
      if (!this.templateString) {
        throw new Error(this.declaredClass + ': templateString is required (import it via ./x.html?raw)');
      }

      var cached = (_TemplatedMixin as any).getCachedTemplate(this.templateString, this._skipNodeCache, this.ownerDocument);

      var node;
      if (lang.isString(cached)) {
        node = domConstruct.toDom(this._stringRepl(cached), this.ownerDocument);
        if (node.nodeType != 1) {
          throw new Error('Invalid template: ' + cached);
        }
      } else {
        node = cached.cloneNode(true);
      }

      this.domNode = node;
    }

    // Call down to _WidgetBase.buildRendering() to get base classes assigned.
    this.inherited(arguments);

    if (!this._rendered) {
      this._fillContent(this.srcNodeRef);
    }

    this._rendered = true;
  },

  _fillContent: function (source: any) {
    // Relocate source contents to the templated container node.
    var dest = this.containerNode;
    if (source && dest) {
      while (source.hasChildNodes()) {
        dest.appendChild(source.firstChild);
      }
    }
  },
});

// Statics hung on the declare result — declare()'s typed return has no static side, so
// cast through `any` for these framework-internal assignments/reads.
// key is templateString; value is either string or DOM tree
(_TemplatedMixin as any)._templateCache = {};

(_TemplatedMixin as any).getCachedTemplate = function (templateString: string, alwaysUseString: boolean, doc: any) {
  // Get a cached template (string if it has ${} vars, else a cloneable DOM tree).
  var tmplts = (_TemplatedMixin as any)._templateCache;
  var key = templateString;
  var cached = tmplts[key];
  if (cached) {
    try {
      if (!cached.ownerDocument || cached.ownerDocument == (doc || document)) {
        return cached;
      }
    } catch (e) { /* squelch */ }
    domConstruct.destroy(cached);
  }

  templateString = string.trim(templateString);
  // C17: strip HTML comments before substitution/toDom. dojo's build stripped
  // template comments; `?raw` keeps them, and a commented-out ${key} would throw.
  templateString = templateString.replace(/<!--[\s\S]*?-->/g, '');

  if (alwaysUseString || templateString.match(/\$\{([^\}]+)\}/g)) {
    // variables present → cache the string
    return (tmplts[key] = templateString);
  } else {
    // no variables → cache the DOM tree
    var node = domConstruct.toDom(templateString, doc);
    if (node.nodeType != 1) {
      throw new Error('Invalid template: ' + templateString);
    }
    return (tmplts[key] = node);
  }
};

export default _TemplatedMixin;
